"use client";

import styles from "./AuthPanel.module.css";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { Button, Flex, Form, Input, message, Modal, Tooltip } from "antd";
import { generateRandomName } from "@/components/generators/user-name-generator";
import { IUserLoginPayload, IUserSignUpPayload } from "@/data/api/auth-data";
import { AppConstants } from "@/app/constants";
import { AppNav } from "@/app/app-nav";
import { API } from "@/app/api";
import {
  changePasswordInitiate,
  logIn,
  signUp,
} from "@/api/fetcher/page-login-fetcher";
import TermsNotice from "@/components/terms/TermsNotice";

enum AuthState {
  SignIn,
  SignUp,
  ChangePassword,
}

export default function AuthPanel({ embedded = false }: { embedded?: boolean }) {
  const router = useRouter();
  const intl = useIntl();
  const [messageApi, contextHolder] = message.useMessage();
  const [modelOpen, setModalOpen] = useState(false);
  const [modalTitleId, setModalTitleId] = useState("");
  const [modalContentId, setModalContentId] = useState("");

  const [authState, setAuthState] = useState(AuthState.SignIn);

  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingSignUp, setLoadingSignUp] = useState(false);
  const [loadingChangePassword, setLoadingChangePassword] = useState(false);

  const handleSignIn = (payload: IUserLoginPayload) => {
    setLoadingLogin(true);

    logIn(
      payload,
      (sessionId) => {
        localStorage.setItem(AppConstants.SessionId, sessionId);
        router.push("/" + AppNav.Monitoring);
      },
      (errorStatus) => {
        setLoadingLogin(false);
        showError(provideErrorText(errorStatus, intl));
      }
    );
  };

  const handleSignUp = (payload: IUserSignUpPayload) => {
    setLoadingSignUp(true);

    signUp(
      payload,
      () => {
        setAuthState(AuthState.SignIn);
        setLoadingSignUp(false);
        setModalTitleId("auth-registration-success-title");
        setModalContentId("auth-registration-success-description");
        setModalOpen(true);
      },
      (errorStatus) => {
        setLoadingSignUp(false);
        showError(provideErrorText(errorStatus, intl));
      }
    );
  };

  const requestPasswordChange = (login: string) => {
    setLoadingChangePassword(true);

    changePasswordInitiate(
      login,
      () => {
        setLoadingChangePassword(false);
        setModalTitleId("auth-change-password-initiate-title");
        setModalContentId("auth-change-password-initiate-title-description");
        setModalOpen(true);
      },
      (errorStatus) => {
        setLoadingChangePassword(false);
        showError(provideErrorText(errorStatus, intl));
      }
    );
  };

  const showError = (errorText: string) =>
    messageApi.open({ type: "error", content: errorText });

  return (
    <>
      {contextHolder}
      <div
        className={`${styles.wrapper} ${embedded ? styles.wrapperEmbedded : ""}`}
      >
        <div className={`${styles.box} ${embedded ? styles.boxEmbedded : ""}`}>
          {authState == AuthState.SignIn && (
            <SignIn
              intl={intl}
              stateLoading={loadingLogin}
              authStateCallback={(state) => setAuthState(state)}
              signInCallback={handleSignIn}
            />
          )}
          {authState == AuthState.SignUp && (
            <SignUp
              intl={intl}
              stateLoading={loadingSignUp}
              authStateCallback={(state) => setAuthState(state)}
              signUpCallback={handleSignUp}
            />
          )}
          {authState == AuthState.ChangePassword && (
            <ChangePassword
              intl={intl}
              stateLoading={loadingChangePassword}
              authStateCallback={(state) => setAuthState(state)}
              requestChangePasswordCallback={requestPasswordChange}
            />
          )}
        </div>
      </div>

      <Modal
        title={<FormattedMessage id={modalTitleId} />}
        centered
        open={modelOpen}
        onOk={() => setModalOpen(false)}
        onCancel={() => setModalOpen(false)}
      >
        <p>
          <FormattedMessage id={modalContentId} />
        </p>
      </Modal>
    </>
  );
}

function SignIn({
  intl,
  stateLoading,
  authStateCallback,
  signInCallback,
}: {
  intl: IntlShape;
  stateLoading: boolean;
  authStateCallback: (state: AuthState) => void;
  signInCallback: (payload: IUserLoginPayload) => void;
}) {
  const [signInValues, setSignInValues] = useState<IUserLoginPayload>({
    login: "",
    password: "",
    totp: "",
  });

  const handleEnterPress = () => {
    if (signInValues.login.length != 0 && signInValues.password.length != 0)
      signInCallback(signInValues);
  };

  return (
    <>
      <h3 className={styles.titleLogin}>
        <FormattedMessage id="auth-login-title" />
      </h3>

      <Form layout="horizontal">
        <Form.Item required label={intl.messages["auth-login"] as string}>
          <Input
            maxLength={64}
            minLength={10}
            type="text"
            placeholder="Login"
            id="login"
            size="middle"
            onChange={(event) => {
              setSignInValues({
                ...signInValues,
                login: event.target.value,
              });
            }}
            disabled={stateLoading}
          />
        </Form.Item>

        <Form.Item required label={intl.messages["auth-password"] as string}>
          <Input.Password
            minLength={10}
            maxLength={64}
            type="password"
            placeholder="Password"
            id="password"
            size="middle"
            onChange={(event) => {
              setSignInValues({
                ...signInValues,
                password: event.target.value,
              });
            }}
            onPressEnter={handleEnterPress}
            disabled={stateLoading}
          />
          {!stateLoading && (
            <div
              className={styles.forgotPasswordBtn}
              onClick={() => authStateCallback(AuthState.ChangePassword)}
            >
              <FormattedMessage id="auth-forgot-password" />
            </div>
          )}
        </Form.Item>

        <Form.Item label={intl.messages["auth-2fa"] as string}>
          <Input
            maxLength={64}
            type="text"
            id="totp"
            placeholder="OTP (option)"
            size="middle"
            onChange={(event) => {
              setSignInValues({
                ...signInValues,
                totp: event.target.value,
              });
            }}
            onPressEnter={handleEnterPress}
            disabled={stateLoading}
          />
        </Form.Item>

        <TermsNotice />

        <Form.Item>
          <Button
            block
            loading={stateLoading}
            size="middle"
            type="primary"
            onClick={() => signInCallback(signInValues)}
            disabled={
              signInValues.login.length == 0 || signInValues.password.length == 0
            }
          >
            <FormattedMessage id="auth-login-title" />
          </Button>
        </Form.Item>
      </Form>

      {!stateLoading && (
        <div className={styles.signUpTitle}>
          <Flex justify="center" vertical={false}>
            <FormattedMessage id="auth-not-registered" />
            {"\u{00A0}"}
            <div
              className={styles.signUpBtn}
              onClick={() => authStateCallback(AuthState.SignUp)}
            >
              <FormattedMessage id="auth-create-an-account" />
            </div>
          </Flex>
        </div>
      )}
    </>
  );
}

function SignUp({
  intl,
  stateLoading,
  authStateCallback,
  signUpCallback,
}: {
  intl: IntlShape;
  stateLoading: boolean;
  authStateCallback: (state: AuthState) => void;
  signUpCallback: (payload: IUserSignUpPayload) => void;
}) {
  const [signUpValues, setSignUpValues] = useState<IUserSignUpPayload>({
    email: "",
    login: "",
    name: generateRandomName(),
    password: "",
  });

  const publicNameLabel = (
    <span className={styles.fieldLabelWithHelp}>
      {intl.messages["auth-public-name"] as string}
      <Tooltip
        title={<FormattedMessage id="auth-public-name-help-desc" />}
        trigger={["hover", "click"]}
      >
        <button
          type="button"
          className={styles.fieldLabelHelpButton}
          aria-label={intl.messages["auth-public-name-help-open"] as string}
        >
          ?
        </button>
      </Tooltip>
    </span>
  );

  return (
    <>
      <h3 className={styles.titleLogin}>
        <FormattedMessage id="auth-register-title" />
      </h3>

      <Form layout="horizontal">
        <Form.Item required label={intl.messages["auth-login"] as string}>
          <Input
            type="text"
            id="Login"
            placeholder="Login"
            size="middle"
            onChange={(event) => {
              setSignUpValues((prev) => ({
                ...prev,
                login: event.target.value,
              }));
            }}
            disabled={stateLoading}
          />
        </Form.Item>

        <Form.Item required label={publicNameLabel}>
          <Input
            type="text"
            id="Public name"
            placeholder="Public name"
            size="middle"
            value={signUpValues.name}
            onChange={(e) => {
              setSignUpValues((prev) => ({
                ...prev,
                name: e.target.value,
              }));
            }}
            disabled={stateLoading}
          />
          {!stateLoading && (
            <div
              className={styles.forgotPasswordBtn}
              onClick={() =>
                setSignUpValues((prev) => ({
                  ...prev,
                  name: generateRandomName(),
                }))
              }
            >
              <FormattedMessage id="auth-public-name-generate" />
            </div>
          )}
        </Form.Item>

        <Form.Item required label={intl.messages["auth-password"] as string}>
          <Input.Password
            type="text"
            id="Password"
            placeholder="Password"
            size="middle"
            onChange={(event) => {
              setSignUpValues((prev) => ({
                ...prev,
                password: event.target.value,
              }));
            }}
            disabled={stateLoading}
          />
        </Form.Item>

        <Form.Item required label={intl.messages["auth-email"] as string}>
          <Input
            type="text"
            id="Email"
            placeholder="E-mail"
            size="middle"
            onChange={(event) => {
              setSignUpValues((prev) => ({
                ...prev,
                email: event.target.value,
              }));
            }}
            disabled={stateLoading}
          />
        </Form.Item>

        <TermsNotice />

        <Form.Item>
          <Button
            block
            size="middle"
            type="primary"
            onClick={() => signUpCallback(signUpValues)}
            disabled={
              signUpValues.login.length == 0 ||
              signUpValues.password.length == 0 ||
              signUpValues.email.length == 0
            }
            loading={stateLoading}
          >
            <FormattedMessage id="auth-create-an-account" />
          </Button>
        </Form.Item>
      </Form>

      {!stateLoading && (
        <div className={styles.signUpTitle}>
          <Flex justify="center" vertical={false}>
            <FormattedMessage id="auth-have-account" />
            {"\u{00A0}"}
            <div
              className={styles.signUpBtn}
              onClick={() => authStateCallback(AuthState.SignIn)}
            >
              <FormattedMessage id="auth-sign-in" />
            </div>
          </Flex>
        </div>
      )}
    </>
  );
}

function ChangePassword({
  intl,
  stateLoading,
  authStateCallback,
  requestChangePasswordCallback,
}: {
  intl: IntlShape;
  stateLoading: boolean;
  authStateCallback: (state: AuthState) => void;
  requestChangePasswordCallback: (login: string) => void;
}) {
  const [login, setLogin] = useState("");

  return (
    <>
      <h3 className={styles.titleLogin}>
        <FormattedMessage id="auth-change-password-title" />
      </h3>

      <Form>
        <Form.Item required label={intl.messages["auth-login"] as string}>
          <Input
            type="text"
            placeholder="Login"
            size="middle"
            onChange={(e) => setLogin(e.target.value)}
            disabled={stateLoading}
          />
        </Form.Item>

        <Form.Item>
          <Button
            block
            size="middle"
            type="primary"
            disabled={login.length == 0}
            onClick={() => requestChangePasswordCallback(login)}
            loading={stateLoading}
          >
            <FormattedMessage id="auth-send-password-link" />
          </Button>
        </Form.Item>
      </Form>

      {!stateLoading && (
        <div className={styles.signUpTitle}>
          <Flex justify="center" vertical={false}>
            <FormattedMessage id="auth-remember-password" />
            {"\u{00A0}"}
            <div
              className={styles.signUpBtn}
              onClick={() => authStateCallback(AuthState.SignIn)}
            >
              <FormattedMessage id="auth-sign-in" />
            </div>
          </Flex>
        </div>
      )}
    </>
  );
}

function provideErrorText(errorStatus: string, intl: IntlShape): string {
  if (errorStatus == API.Status.Unknown) {
    return intl.messages["api-response-status-unknown"] as string;
  } else if (errorStatus == API.Status.LoginInvalidPassword) {
    return intl.messages[
      "api-response-status-login-invalid-password"
    ] as string;
  } else if (errorStatus == API.Status.LoginUserNotActivated) {
    return intl.messages["api-response-status-login-not-activated"] as string;
  } else if (errorStatus == API.Status.LoginInvalid2FACode) {
    return intl.messages[
      "api-response-status-login-totp-code-invalid"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserLoginFormatInvalid) {
    return intl.messages[
      "api-response-status-create-user-invalid-login-format"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserPasswordFormatInvalid) {
    return intl.messages[
      "api-response-status-create-user-invalid-password-format"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserEmailFormatInvalid) {
    return intl.messages[
      "api-response-status-create-user-invalid-email-format"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserNameFormatInvalid) {
    return intl.messages[
      "api-response-status-create-user-invalid-name-format"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserDuplicateEmail) {
    return intl.messages[
      "api-response-status-create-user-duplicate-email"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserDuplicateLogin) {
    return intl.messages[
      "api-response-status-create-user-duplicate-login"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserSmtpError) {
    return intl.messages[
      "api-response-status-create-user-email-send-error"
    ] as string;
  } else if (errorStatus == API.Status.CreateUserEmailSendError) {
    return intl.messages[
      "api-response-status-create-user-admin-email-send-error"
    ] as string;
  }
  return intl.messages["api-response-status-unknown"] as string;
}
